export type MessagePreview = 'hidden' | 'sender' | 'sender_preview';
export type NotificationSection = 'messages' | 'meetups' | 'calls' | 'financial';
export type NotificationTopic =
  | 'meetup_requests'
  | 'meetup_updates'
  | 'meetup_reminders'
  | 'scheduled_call_requests'
  | 'scheduled_call_reminders'
  | 'incoming_calls'
  | 'missed_calls'
  | 'financial_earnings'
  | 'financial_refunds'
  | 'financial_payouts';

export type NotificationTopicPreferences = Record<NotificationTopic, boolean>;

// 9.26 / D-637 — bütün varsayılanlar kapalı; sunucudaki sabitlerin aynası.
// Sunucuya ulaşılamadığında ekranın göstereceği değer de bu, o yüzden ikisi
// ayrışırsa ekran kişiye yanlış söylerdi.
export const DEFAULT_NOTIFICATION_TOPIC_PREFERENCES: Readonly<NotificationTopicPreferences> = Object.freeze({
  meetup_requests: false,
  meetup_updates: false,
  meetup_reminders: false,
  scheduled_call_requests: false,
  scheduled_call_reminders: false,
  incoming_calls: false,
  missed_calls: false,
  financial_earnings: false,
  financial_refunds: false,
  financial_payouts: false,
});

export type NotificationPreferences = {
  push_enabled: boolean;
  messages_enabled: boolean;
  meetups_enabled: boolean;
  calls_enabled: boolean;
  financial_enabled: boolean;
  reactions_enabled: boolean;
  message_preview: MessagePreview;
  topics: NotificationTopicPreferences;
};

export type NotificationPreferencePatch = Partial<Omit<NotificationPreferences, 'topics'>> & {
  topics?: Partial<NotificationTopicPreferences>;
};

export const DEFAULT_NOTIFICATION_PREFERENCES: Readonly<NotificationPreferences> = Object.freeze({
  push_enabled: false,
  messages_enabled: false,
  meetups_enabled: false,
  calls_enabled: false,
  financial_enabled: false,
  reactions_enabled: false,
  message_preview: 'sender',
  topics: DEFAULT_NOTIFICATION_TOPIC_PREFERENCES,
});

export const NOTIFICATION_SECTIONS = Object.freeze([
  { id: 'messages', field: 'messages_enabled', labelKey: 'notifications.messages', infoKey: 'notifications.messagesInfo' },
  { id: 'meetups', field: 'meetups_enabled', labelKey: 'notifications.meetups', infoKey: 'notifications.meetupsInfo' },
  { id: 'calls', field: 'calls_enabled', labelKey: 'notifications.calls', infoKey: 'notifications.callsInfo' },
  { id: 'financial', field: 'financial_enabled', labelKey: 'notifications.financial', infoKey: 'notifications.financialInfo' },
] as const);

export const NOTIFICATION_TOPICS: Readonly<Record<Exclude<NotificationSection, 'messages'>, ReadonlyArray<{
  field: NotificationTopic;
  labelKey: string;
  infoKey: string;
}>>> = Object.freeze({
  meetups: [
    { field: 'meetup_requests', labelKey: 'notifications.meetupRequests', infoKey: 'notifications.meetupRequestsInfo' },
    { field: 'meetup_updates', labelKey: 'notifications.meetupUpdates', infoKey: 'notifications.meetupUpdatesInfo' },
    { field: 'meetup_reminders', labelKey: 'notifications.meetupReminders', infoKey: 'notifications.meetupRemindersInfo' },
  ],
  calls: [
    { field: 'incoming_calls', labelKey: 'notifications.incomingCalls', infoKey: 'notifications.incomingCallsInfo' },
    { field: 'scheduled_call_requests', labelKey: 'notifications.callRequests', infoKey: 'notifications.callRequestsInfo' },
    { field: 'scheduled_call_reminders', labelKey: 'notifications.callReminders', infoKey: 'notifications.callRemindersInfo' },
    { field: 'missed_calls', labelKey: 'notifications.missedCalls', infoKey: 'notifications.missedCallsInfo' },
  ],
  financial: [
    { field: 'financial_earnings', labelKey: 'notifications.financialEarnings', infoKey: 'notifications.financialEarningsInfo' },
    { field: 'financial_refunds', labelKey: 'notifications.financialRefunds', infoKey: 'notifications.financialRefundsInfo' },
    { field: 'financial_payouts', labelKey: 'notifications.financialPayouts', infoKey: 'notifications.financialPayoutsInfo' },
  ],
});

export const MESSAGE_PREVIEW_OPTIONS = Object.freeze([
  { value: 'hidden', labelKey: 'notifications.previewHidden', infoKey: 'notifications.previewHiddenInfo' },
  { value: 'sender', labelKey: 'notifications.previewSender', infoKey: 'notifications.previewSenderInfo' },
  { value: 'sender_preview', labelKey: 'notifications.previewText', infoKey: 'notifications.previewTextInfo' },
] as const);

/**
 * "Bildirimleri aç" düğmesinin uyguladığı tercih (9.26 · D-638).
 *
 * D-637'den önceki "hepsi açık" hâli: ana anahtar, dört kategori ve on olay
 * türünün hepsi. Tepkiler BİLEREK dışarıda — o, eskiden de kapalıydı ve
 * kişinin kendi kararı. Tek tanım burada; kart ve ileride başka bir yüzey aynı
 * şeyi açsın diye.
 */
export const ENABLE_ALL_NOTIFICATIONS_PATCH: Readonly<NotificationPreferencePatch> = Object.freeze({
  push_enabled: true,
  messages_enabled: true,
  meetups_enabled: true,
  calls_enabled: true,
  financial_enabled: true,
  topics: {
    meetup_requests: true,
    meetup_updates: true,
    meetup_reminders: true,
    scheduled_call_requests: true,
    scheduled_call_reminders: true,
    incoming_calls: true,
    missed_calls: true,
    financial_earnings: true,
    financial_refunds: true,
    financial_payouts: true,
  },
});

/** Kart "açık" desin mi: ana anahtar ve mesaj ile arama bildirimleri açıksa. */
export function notificationsLookEnabled(p: Pick<NotificationPreferences, 'push_enabled' | 'messages_enabled' | 'calls_enabled'>): boolean {
  return p.push_enabled && p.messages_enabled && p.calls_enabled;
}
